import React from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import { HomePage } from './home/HomePage'
import { NotFoundPage } from './not-found/NotFoundPage'
import { LoginPage } from './login/LoginPage'
import { UsersPage } from './users/UsersPage'
import { BuildingTemplatesPage } from './building-template/BuildingTemplatesPage'
import { useAuthentication } from './authentication/AuthenticationProvider'
import { DefaultLayout } from './layout/DefaultLayout'
import { ProjectLayout } from './project/ProjectLayout'
import { ProjectHourSeriesPage } from './project-hour-series/ProjectHourSeriesPage'
import { ProjectSettingsPage } from './project/ProjectSettingsPage'
import { useProfile } from './profile/ProfileProvider'
import { ProjectReportPage } from './project/ProjectReportPage'
import { DeletedProjectsPage } from './deleted-project/DeletedProjectsPage'
import { Loader } from './common/Loader'
import { UserGroupsPage } from './user-group/UserGroupsPage'
import { GlobalTemperatureCollectionsPage } from './global-temperature-collections/GlobalTemperatureCollectionsPage'
import { AdminLayout } from './layout/AdminLayout'
import { ProjectPage } from './project/ProjectPage'
import { CostsLayout } from './layout/CostsLayout'
import { CostsPage } from './costs/CostsPage'
import { CostsResultsPage } from './costs/CostsResultsPage'
import { GlobalPricingModelsPage } from './global-pricing-models/GlobalPricingModelsPage'
import { EerLayout } from './layout/EerLayout'
import { EerPage } from './eer/EerPage'
import { CarbonFootprintLayout } from './layout/CarbonFootprintLayout'
import { CarbonFootprintPage } from './carbon-footprint/CarbonFootprintPage'
import { CarbonFootprintResultsPage } from './carbon-footprint/CarbonFootprintResultsPage'
import { ProjectSubDataLayout } from './layout/ProjectSubDataLayout'
import { BuildingEnergyModelsPage } from './building-energy-models/BuildingEnergyModelsPage'

export function Routes(): React.ReactElement {
  const { isInitialized, isLoggedIn } = useAuthentication()
  const { isFetched, isAdmin } = useProfile()

  if (!isInitialized) {
    return <div />
  }

  if (!isLoggedIn) {
    return (
      <Switch>
        <Route exact path={['/login']}>
          <DefaultLayout>
            <Switch>
              <Route path="/login" component={LoginPage} />
            </Switch>
          </DefaultLayout>
        </Route>

        <Redirect to="/login" />
      </Switch>
    )
  }

  if (!isFetched) {
    return <Loader />
  }

  const adminPaths = [
    '/users',
    '/user-groups',
    '/building-templates',
    '/global-temperature-collections',
    '/global-pricing-models',
    '/deleted-projects',
  ]
  const adminRoutes = (
    <>
      <Route path="/users" component={UsersPage} />
      <Route path="/user-groups" component={UserGroupsPage} />
      <Route path="/building-templates" component={BuildingTemplatesPage} />
      <Route
        path="/global-temperature-collections"
        component={GlobalTemperatureCollectionsPage}
      />
      <Route
        path="/global-pricing-models"
        component={GlobalPricingModelsPage}
      />
      <Route path="/deleted-projects" component={DeletedProjectsPage} />
    </>
  )

  const projectSubPaths = [
    '/projects/:id/costs',
    '/projects/:id/carbonfootprint',
    '/projects/:id/eer',
  ]

  return (
    <Switch>
      <Route exact path="/">
        <DefaultLayout>
          <Route exact path="/" component={HomePage} />
        </DefaultLayout>
      </Route>

      {isAdmin ? (
        <Route exact path={adminPaths}>
          <AdminLayout>
            <Switch>{adminRoutes}</Switch>
          </AdminLayout>
        </Route>
      ) : null}

      <Route path={projectSubPaths}>
        <ProjectSubDataLayout>
          <Switch>
            <Route path="/projects/:id/costs">
              <CostsLayout>
                <Switch>
                  <Route
                    exact
                    path="/projects/:id/costs"
                    component={CostsPage}
                  />
                  <Route
                    exact
                    path="/projects/:id/costs/results"
                    component={CostsResultsPage}
                  />
                </Switch>
              </CostsLayout>
            </Route>

            <Route path="/projects/:id/carbonfootprint">
              <CarbonFootprintLayout>
                <Switch>
                  <Route
                    exact
                    path="/projects/:id/carbonfootprint"
                    component={CarbonFootprintPage}
                  />
                  <Route
                    exact
                    path="/projects/:id/carbonfootprint/results"
                    component={CarbonFootprintResultsPage}
                  />
                </Switch>
              </CarbonFootprintLayout>
            </Route>

            <Route path="/projects/:id/eer">
              <EerLayout>
                <Switch>
                  <Route exact path="/projects/:id/eer" component={EerPage} />
                </Switch>
              </EerLayout>
            </Route>
          </Switch>
        </ProjectSubDataLayout>
      </Route>

      <Route path="/projects/:id">
        <ProjectLayout>
          <Switch>
            <Route exact path="/projects/:id" component={ProjectPage} />
            <Route
              path="/projects/:id/hour-series"
              component={ProjectHourSeriesPage}
            />
            <Route
              path="/projects/:id/settings"
              component={ProjectSettingsPage}
            />
            <Route path="/projects/:id/report" component={ProjectReportPage} />
            <Route
              path="/projects/:id/energymodels"
              component={BuildingEnergyModelsPage}
            />
          </Switch>
        </ProjectLayout>
      </Route>

      <Route path="/login">
        <Redirect to="/" />
      </Route>

      <Route path="*" component={NotFoundPage} />
    </Switch>
  )
}
